import { Injectable } from '@angular/core';
import { AlarmAgentApiClient, AuthService, JWT } from '.';
import { UserRole } from '../enums/user-role.enum';
import { sortBy } from 'lodash';

@Injectable({
	providedIn: 'root',
})
export class CompanyService {
	private API_ROUTE = 'company';

	constructor(
		private http: AlarmAgentApiClient,
		private readonly authService: AuthService
	) {}

	async getCompanies(deactivateLoader = false): Promise<CompanyIdentifier[]> {
		return this.http
			.get(`${this.API_ROUTE}`, {
				headers: {
					'deactivate-loader': deactivateLoader ? 'true' : 'false',
				},
			})
			.toPromise();
	}

	async getCompaniesWithChildren(): Promise<CompanyIdentifier[]> {
		return this.http.get(`${this.API_ROUTE}/parents`).toPromise();
	}

	async getCompanyWithChildren(
		companyId: number
	): Promise<CompanyIdentifier[]> {
		return this.http
			.get(`${this.API_ROUTE}/companyAndChildren?id=${companyId}`)
			.toPromise();
	}

	async getRtusForCompany(
		companyId: number,
		page: number = 0,
		pageSize: number = 10,
		deactivateLoader = false
	): Promise<CompanyRTUs> {
		return this.http
			.get(
				`${this.API_ROUTE}/${companyId}/rtus?page=${page}&pageSize=${pageSize}`,
				{
					headers: {
						'deactivate-loader': deactivateLoader
							? 'true'
							: 'false',
					},
				}
			)
			.toPromise();
	}

	getCompanyLogo(companyId: number) {
		return this.http.get(`${this.API_ROUTE}/companyLogo?id=${companyId}`, {
			responseType: 'blob',
		});
	}

	async getCompaniesByUserType(addAllCompanies = true) {
		let companies = [];
		const dataUser = this.authService.getAuthData();
		if (dataUser.roles.includes(UserRole.RacoAdmin)) {
			companies = await this.getCompanies();
		} else {
			companies = await this.getCompanyWithChildren(dataUser.companyId);
		}
		companies = sortBy(companies, 'name');
		if (addAllCompanies) {
			companies.unshift({ id: 0, name: 'All Companies' });
		}

		return companies;
	}
}

export class CompanyIdentifier {
	id: number = 0;
	name: string = '';
}

export class CompanyRTUs {
	count: number = 0;
	page: number = 1;
	pageSize?: number;
	rtus?: CompanyRtu[];
}

export class CompanyRtu {
	id?: number;
	companyId?: number;
	rtuname?: string;
	assetTag?: string;
	configTypeId?: number;
	csid?: string;
	expirationDate?: Date;
	expirationDateFormat?: string;
	firmwareVersionName?: string;
	firmwareRevisionId?: number;
	cellularTechnology?: string;
	servicePlanEndDate?: Date;
	servicePlanEndDateFormatted?: string;
	magicWord?: string;
	rsn?: string;
	lastContactTime?: Date;
	lastContactTimeFormatted?: string;
	channels?: RtuChannel[];
}

export interface RtuChannel {
	id: number;
	channelNumber: number;
	message1: string;
	message2: string;
	mode: number;
	realtimeMode: number;
	maximumValue?: number;
	minimumValue?: number;
	scalingGain: number;
	scalingOffset: number;
	maximumScaling: number;
	minimumScaling: number;
}
